import { useEffect, useState } from 'react';
import { listTasks } from '../utils/apiClient';
import type { Task } from '../types';

/**
 * Cached combined-task list backing the sidebar badges. Sidebar applies the
 * active viewer filter on top so badge counts match what the user sees in
 * each group when they click in.
 */

const listeners = new Set<() => void>();
const EMPTY_TASKS: Task[] = [];
let cache: Task[] | null = null;
let inflight: Promise<void> | null = null;

export async function refreshTaskCounts(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      try {
        cache = await listTasks('combined');
      } catch {
        // Cold-start race: the async tokenProvider can resolve to null on
        // the first call before AuthProvider's getSession settles, yielding a
        // 401. Retry once so a transient miss doesn't blank sidebar badges
        // for the whole session — the only other rescue path is InboxView's
        // piggy-back call, which never fires if the user lands elsewhere.
        await new Promise((r) => setTimeout(r, 600));
        cache = await listTasks('combined');
      }
      for (const l of listeners) l();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useTaskCounts(): { tasks: Task[]; refresh: () => Promise<void> } {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = (): void => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  useEffect(() => {
    if (!cache && !inflight) {
      void refreshTaskCounts().catch(() => {
        // Leave tasks empty; sidebar just omits badges.
      });
    }
  }, []);
  return { tasks: cache ?? EMPTY_TASKS, refresh: refreshTaskCounts };
}
